import type React from "react";
import { useCallback } from "react";
import { Avatar, AvatarFallback } from "@planta-v2/pds-ui/avatar";
import { Button } from "@planta-v2/pds-ui/button";
import { DateUtils } from "@frontend/shared/presentation/utils/date.utils";
import { PlantaAvatarImage } from "@planta-v2/pds-ui/planta-avatar-image";
import { StringUtils } from "@frontend/shared/presentation/utils/string.utils";
import { useScheduledImageSrc } from "@frontend/shared/presentation/hooks/useScheduledImageSrc";
import type { ConnectionRequestSummary } from "@shared/schemas/connection-request.schemas";
import {
  ConnectionRequestIdPublic,
  type EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { formatProfileSubtitle } from "@shared/utils/profile-subtitle.utils";
import { IntroRequestStatus } from "@shared/schemas/intro-request-state.schemas";
import { i18n } from "@frontend/i18n";
import { ProfileSubtitleWithLink } from "./ProfileSubtitleWithLink";

interface ConnectionRequestCardProps {
  readonly request: ConnectionRequestSummary;
  readonly onAccept: (requestIdPublic: string) => void;
  readonly onIgnore: (requestIdPublic: string) => void;
  readonly onViewProfile: (
    participantIdPublic: EventParticipantIdPublic,
  ) => void;
  readonly isAccepting?: boolean;
  readonly isIgnoring?: boolean;
}

function isPendingRequest(status: IntroRequestStatus): boolean {
  return (
    status === IntroRequestStatus.PENDING ||
    status === IntroRequestStatus.SENT ||
    status === IntroRequestStatus.SEEN
  );
}

function PendingBodyContent({
  message,
}: {
  readonly message: { content: string } | null;
}): React.JSX.Element | null {
  const bodyText = message?.content;

  if (bodyText === undefined) {
    return null;
  }

  return (
    <p className="mt-3 text-sm text-foreground leading-relaxed break-words">
      {bodyText}
    </p>
  );
}

export function ConnectionRequestCard({
  request,
  onAccept,
  onIgnore,
  onViewProfile,
  isAccepting = false,
  isIgnoring = false,
}: ConnectionRequestCardProps): React.JSX.Element {
  const status = request.typeData.status;
  const isPending = isPendingRequest(status);
  const isAccepted = status === IntroRequestStatus.ACCEPTED;
  const isDeclined = status === IntroRequestStatus.DECLINED;

  const name = request.fromUserName;
  const photoUrl = request.fromUserPhotoUrl;
  const headline = request.fromUserHeadline;
  const company = request.fromUserCompany;
  const message = request.typeData.message;
  const fromParticipantIdPublic = request.fromParticipantIdPublic;

  const { ref, scheduledSrc, onLoadingStatusChange } =
    useScheduledImageSrc(photoUrl);

  const requestedTimeAgo = DateUtils.formatRelativeDate(
    request.createdAt,
    i18n.getLocale(),
  );

  const subtitle = formatProfileSubtitle(
    { title: headline, companyName: company },
    ({ position, company: companyName }) =>
      i18n.t("shared.profile.subtitle.withCompany", {
        position,
        company: companyName,
      }),
  );

  const handleCardClick = useCallback((): void => {
    onViewProfile(fromParticipantIdPublic);
  }, [onViewProfile, fromParticipantIdPublic]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleCardClick();
      }
    },
    [handleCardClick],
  );

  const handleAccept = useCallback(
    (e: React.MouseEvent): void => {
      e.stopPropagation();
      onAccept(ConnectionRequestIdPublic.unwrap(request.idPublic));
    },
    [onAccept, request.idPublic],
  );

  const handleIgnore = useCallback(
    (e: React.MouseEvent): void => {
      e.stopPropagation();
      onIgnore(ConnectionRequestIdPublic.unwrap(request.idPublic));
    },
    [onIgnore, request.idPublic],
  );

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      className="w-full border-b border-foreground/10 py-4 text-left transition-colors last:border-b-0 hover:bg-accent/50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring"
    >
      {/* Header row: Avatar, Name/Status, Full Profile link */}
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10 shrink-0 rounded-full border border-foreground/10">
          {scheduledSrc !== null ? (
            <PlantaAvatarImage
              src={scheduledSrc}
              alt={name}
              onLoadingStatusChange={onLoadingStatusChange}
            />
          ) : null}
          <AvatarFallback className="bg-muted-foreground/20 font-serif text-sm font-normal text-foreground/75">
            {StringUtils.getInitials(name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex flex-col min-w-0 flex-1 gap-0.5">
          <span className="font-serif text-base font-normal text-foreground leading-tight truncate">
            {name}
          </span>
          {/* Accepted: show status */}
          {/* Declined: show status with undo */}
          {/* Pending: show headline + Full profile on same line */}
          {isAccepted ? (
            <span className="text-pds-2xs text-muted-foreground leading-tight">
              {i18n.t("user.directory.requests.status.accepted")}
            </span>
          ) : isDeclined ? (
            <span className="text-pds-2xs text-muted-foreground leading-tight">
              {i18n.t("user.directory.requests.status.ignored")}
            </span>
          ) : (
            <ProfileSubtitleWithLink subtitle={subtitle} />
          )}
        </div>
      </div>

      {/* Pending state: show the requester's note */}
      {isPending && <PendingBodyContent message={message} />}

      {/* Action buttons - only for pending requests */}
      {isPending && (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <Button
            type="button"
            onClick={handleAccept}
            disabled={isAccepting || isIgnoring}
            className="shrink-0"
          >
            {isAccepting
              ? i18n.t("user.directory.requests.accepting")
              : i18n.t("user.directory.requests.accept")}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleIgnore}
            disabled={isAccepting || isIgnoring}
            className="shrink-0"
          >
            {isIgnoring
              ? i18n.t("user.directory.requests.ignoring")
              : i18n.t("user.directory.requests.ignore")}
          </Button>
        </div>
      )}

      {/* Requested timestamp - below action buttons for pending requests */}
      {isPending && (
        <p
          data-testid="connection-request-timestamp"
          className="mt-3 text-pds-2xs text-muted-foreground"
        >
          {i18n.t("user.directory.requests.requestedAgo", {
            time: requestedTimeAgo,
          })}
        </p>
      )}
    </div>
  );
}
